import { useEffect, useMemo, useState } from "react";
import { format, startOfWeek, addDays, isSameDay, endOfWeek } from "date-fns";
import { cn, formatDate, getWeekDates } from "../../lib/utils";

import ScheduleNavBar, {
  ScheduleViewMode,
} from "../../components/admin/schedule/ScheduleNavBar";
import FilterToolbar from "../../components/admin/schedule/FilterToolbar";
import TimelineView from "../../components/admin/schedule/TimelineView";
import WeekOverview from "../../components/admin/schedule/WeekOverview";
import { useClinics } from "../../hooks/useClinics";
import { useDoctors } from "../../hooks/useDoctors";
import { useSchedules } from "../../hooks/schedule/useSchedule";
import {
  CreateScheduleSlotCommand,
  ScheduleClient,
  ScheduleSlotDto,
  SlotStatus,
  TimeRangeSlotDto,
  UpdateScheduleSlotTimeRangeCommand,
} from "../../web-api-client";
import { useCreateSchedule } from "../../hooks/schedule/useCreateSchedule";
import { useUpdateSchedule } from "../../hooks/schedule/useUpdateSchedule";
import { useDeleteSchedule } from "../../hooks/schedule/useDeleteSchedule";
import { toUserMessage } from "../../utils/toUserMessage";
import { useToast } from "../../hooks/use-toast";
import { useStickyLoading } from "../../hooks/useStickyLoading";
import LoadingIndicator from "../../components/LoadingIndicator";
import { CreateSlotSidebar } from "../../components/admin/schedule/CreateSlotSidebar";
import { useForm } from "react-hook-form";
import { useScheduleConflictCheck } from "../../hooks/schedule/useScheduleConflictCheck";
import { TIME_SLOTS } from "../../shared/schema";
import { useDebounce } from "../../hooks/useDebounce";
import { useQueryClient } from "@tanstack/react-query";
import { makeFetchWithAuth } from "../../auth/authFetch";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { useNavigate } from "react-router-dom";
import { useUpdateTimeRangeStatus } from "../../hooks/schedule/useUpdateTimeRangeStatus";
import { useDeleteTimeRange } from "../../hooks/schedule/useDeleteTimeRange";
import { buildUpdateSchedulePayload } from "../../data/schedulePayload";
import { appConfig } from "../../appconfig";

export default function SchedulePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const updateSchedule = useUpdateSchedule();
  const updateTimeRangeStatus = useUpdateTimeRangeStatus();
  const deleteDoctorSlot = useDeleteSchedule();
  const deleteTimeRange = useDeleteTimeRange();

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<ScheduleViewMode>("week");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("all");
  const [selectedRoom, setSelectedRoom] = useState("all");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [createSidebarOpen, setCreateSidebarOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<ScheduleSlotDto | undefined>();
  const [creatingSlot, setCreatingSlot] = useState<
    CreateScheduleSlotCommand | undefined
  >();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);

  const [doctorKeyword, setDoctorKeyword] = useState("");
  const debouncedDoctorKeyword = useDebounce(doctorKeyword, 300);

  const [sidebarDoctorKeyword, setSidebarDoctorKeyword] = useState("");
  const debouncedSidebarDoctorKeyword = useDebounce(sidebarDoctorKeyword, 300);

  const [weekOverviewDoctorKeyword, setWeekOverviewDoctorKeyword] =
    useState("");
  const debouncedWeekOverviewDoctorKeyword = useDebounce(
    weekOverviewDoctorKeyword,
    300,
  );

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = addDays(weekStart, 4);

  const { toast } = useToast();

  const { data: clinics = [], isLoading: isClinicLoading } = useClinics(false);

  const form = useForm<CreateScheduleSlotCommand>({
    defaultValues: {
      clinicExtId: 0,
      dates: [],
      startTime: "",
      endTime: "",
    },
  });

  const conflictInput = {
    clinicExtId: form.getValues("clinicExtId"),
    dates: form.getValues("dates"),
    startTime: form.getValues("startTime"),
    endTime: form.getValues("endTime"),
  };

  const debouncedConflictInput = useDebounce(conflictInput, 300);

  const {
    data: conflictData = [],
    isLoading: isCheckingConflicts,
    error: conflictError,
  } = useScheduleConflictCheck({
    clinicExtId: debouncedConflictInput.clinicExtId,
    dates: debouncedConflictInput.dates,
    startTime: debouncedConflictInput.startTime,
    endTime: debouncedConflictInput.endTime,
  });

  const hasConflicts = conflictData.length > 0 || conflictError != null;
  const conflicts = useMemo(() => {
    if (conflictError) {
      conflictData.push(String(conflictError));
    }
    return conflictData;
  }, [conflictData, conflictError]);

  const {
    doctors,
    avatars,
    avatarErrors,
    isFetching: isDoctorLoading,
  } = useDoctors({
    date: "",
    specialty: null,
    availableOnly: false,
    searchTerm: debouncedDoctorKeyword || "",
  });

  const { doctors: allDoctors } = useDoctors({
    date: "",
    specialty: null,
    availableOnly: false,
    searchTerm: "",
    pageNumber: 1,
    pageSize: 9999,
  });

  const { doctors: sidebarDoctors, isFetching: isSidebarDoctorLoading } =
    useDoctors({
      date: "",
      specialty: null,
      availableOnly: false,
      searchTerm: debouncedSidebarDoctorKeyword || "",
    });

  const {
    doctors: weekOverviewDoctorSuggestions,
    isFetching: isWeekOverviewDoctorLoading,
  } = useDoctors({
    date: "",
    specialty: null,
    availableOnly: false,
    searchTerm: debouncedWeekOverviewDoctorKeyword || "",
  });

  const doctorOptions = useMemo(
    () =>
      doctors.map((d) => ({
        value: d.doctorId ?? d.name ?? "",
        label: d.name ?? "",
      })),
    [doctors],
  );

  const clinicOptions = useMemo(
    () =>
      clinics.map((d) => ({
        value: d.clinicId ?? 0,
        label: d.clinicName ?? "",
      })),
    [clinics],
  );

  const { data: schedules, isLoading: isScheduleLoading } = useSchedules({
    clinicId: selectedRoom !== "all" ? Number(selectedRoom) : undefined,
    doctorId: selectedDoctor !== "all" ? selectedDoctor : undefined,
    fromDate: weekStart,
    toDate: weekEnd,
  });

  const createMutation = useCreateSchedule();
  const updateMutation = useUpdateSchedule();
  const deleteMutation = useDeleteSchedule();

  const isLoading =
    isClinicLoading ||
    isDoctorLoading ||
    isScheduleLoading ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    isCheckingConflicts;

  const overlayVisible = useStickyLoading(isLoading, {
    showDelay: 150,
    minVisible: 300,
  });

  const startHour = 7;
  const endHour = 16;
  const intervalMinutes = 30;

  const timeSlots: Date[] = [];

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      const time = new Date(currentDate);
      time.setHours(hour, minute, 0, 0);
      timeSlots.push(time);
    }
  }

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedDoctor("all");
    setSelectedRoom("all");
    setSelectedDepartment("all");
  };

  const handleAddSlot = () => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    const endTime = new Date(now.getTime() + 60 * 60000);

    var slot = new ScheduleSlotDto({
      id: 0,
      doctorExtId: "",
      clinicExtId: 0,
      startTime: now,
      endTime,
      capacity: 5,
      bookedCount: 0,
    });

    setEditingSlot(slot);

    setCreateSidebarOpen(true);
  };

  // CREATE
  const handleCreateSlot = async (data: CreateScheduleSlotCommand) => {
    try {
      await createMutation.mutateAsync(data);

      toast({
        variant: "success",
        title: String("Thêm mới thành công"),
        description: "",
        duration: 3000,
      });

      setCreateSidebarOpen(false);
      setEditingSlot(undefined);
    } catch (err: any) {
      console.error("Lưu lịch khám thất bại:", toUserMessage(err));

      const errorMsg = await toUserMessage(err);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: errorMsg,
        duration: 3000,
      });
    }
  };

  const handleDayClick = (date: Date, clinicExtId: number) => {
    // setCurrentDate(date);

    var slot = new CreateScheduleSlotCommand({
      clinicExtId,
      dates: [formatDate(date)],
      startTime: form.getValues("startTime"),
      endTime: form.getValues("endTime"),
    });

    setCreatingSlot(slot);

    setCreateSidebarOpen(true);
    // setViewMode("day");
  };

  const filteredRooms = clinics.filter((room) => {
    if (selectedRoom !== "all" && room.clinicId !== Number(selectedRoom))
      return false;

    return true;
  });

  const filteredSlots: TimeRangeSlotDto[] = (schedules ?? [])
    .filter((slot) =>
      selectedRoom === "all" ? true : slot.clinicExtId === Number(selectedRoom),
    )
    .map((slot) => {
      const doctors =
        selectedDoctor === "all"
          ? (slot.doctors ?? [])
          : (slot.doctors ?? []).filter(
              (d) => d.doctorExtId === selectedDoctor,
            );

      const clone = new TimeRangeSlotDto();
      clone.init({ ...slot.toJSON(), doctors });

      return clone;
    })
    .filter(
      (slot) => selectedDoctor === "all" || (slot.doctors?.length ?? 0) > 0,
    );

  useEffect(() => {
    const storedView = localStorage.getItem(
      "scheduleViewMode",
    ) as ScheduleViewMode | null;

    const raw = localStorage.getItem("scheduleViewDate");

    let storedViewDate: Date | null = null;
    if (raw) {
      const d = new Date(raw); // raw là ISO string
      if (!Number.isNaN(d.getTime())) {
        storedViewDate = d;
      }
    }

    if (storedView) {
      setViewMode(storedView);
    }

    if (storedViewDate) {
      setCurrentDate(storedViewDate);
    }
  }, []);

  useEffect(() => {
    if (sidebarOpen) {
      setCreateSidebarOpen(false);
    }
  }, [sidebarOpen]);

  useEffect(() => {
    if (createSidebarOpen) {
      setSidebarOpen(false);
    }
  }, [createSidebarOpen]);

  useEffect(() => {
    if (!creatingSlot) return;

    form.reset({
      clinicExtId: creatingSlot.clinicExtId ?? 0,
      dates: creatingSlot?.dates ?? [],
      startTime: creatingSlot?.startTime ?? "",
      endTime: creatingSlot?.endTime ?? "",
    });
  }, [creatingSlot, form]);

  useEffect(() => {
    localStorage.setItem("scheduleViewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("scheduleViewDate", currentDate.toISOString());
  }, [currentDate]);

  const handleTimeSlotClick = (slot: (typeof TIME_SLOTS)[number]) => {
    setSelectedTimeSlot(slot.label);
    form.setValue("startTime", slot.start);
    form.setValue("endTime", slot.end);
  };

  const handleDateToggle = (dateStr: string, checked: boolean) => {
    const currentDates = form.getValues("dates") ?? [];
    if (checked) {
      form.setValue("dates", [...currentDates, dateStr]);
    } else {
      form.setValue(
        "dates",
        currentDates.filter((d) => d !== dateStr),
      );
    }
  };

  const slotKey = (s: TimeRangeSlotDto) =>
    `${s.clinicExtId}|${format(s.startTime!, "yyyy-MM-dd")}|${format(s.startTime!, "HH:mm")}-${format(s.endTime!, "HH:mm")}`;

  const dateWithHm = (base: Date, hm: string) => {
    const ymd = format(base, "yyyy-MM-dd");
    return new Date(`${ymd}T${hm}:00`);
  };

  const handleUpdateTimeRange = async (
    slot: TimeRangeSlotDto,
    next: { start: string; end: string },
  ) => {
    const client = new ScheduleClient(appConfig.apiBaseUrl, {
      fetch: makeFetchWithAuth(fetchWithTimeout(), {
        onUnauthorized: () =>
          navigate("/login", {
            replace: true,
            state: { from: window.location.pathname },
          }),
      }),
    });

    const cmd = new UpdateScheduleSlotTimeRangeCommand();
    cmd.clinicExtId = slot.clinicExtId!;
    cmd.localDate = format(slot.startTime!, "dd/MM/yyyy");
    cmd.oldStartTime = format(slot.startTime!, "HH:mm");
    cmd.oldEndTime = format(slot.endTime!, "HH:mm");
    cmd.newStartTime = next.start;
    cmd.newEndTime = next.end;

    const oldKey = slotKey(slot);

    queryClient.setQueriesData(
      {
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === "schedules",
      },
      (old: any) => {
        if (!Array.isArray(old)) return old;

        return old.map((s: any) => {
          const json = typeof s?.toJSON === "function" ? s.toJSON() : s;
          const tmp = new TimeRangeSlotDto();
          tmp.init(json);

          if (!tmp.startTime || !tmp.endTime) return s;
          if (slotKey(tmp) !== oldKey) return s;

          const updatedJson = {
            ...json,
            startTime: dateWithHm(tmp.startTime, next.start),
            endTime: dateWithHm(tmp.startTime, next.end),
          };

          const clone = new TimeRangeSlotDto();
          clone.init(updatedJson);
          return clone;
        });
      },
    );

    try {
      const updated = await client.updateTimeRangeSlot(cmd);

      if (!updated || updated <= 0) {
        throw new Error("Cập nhật không thành công.");
      }

      await queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === "schedules",
      });

      toast({
        variant: "success",
        title: "Đã cập nhật khung giờ",
        duration: 2500,
      });
      return updated;
    } catch (error: any) {
      await queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === "schedules",
      });

      const msg = await toUserMessage(error);
      toast({
        variant: "destructive",
        title: "Thất bại",
        description: msg,
        duration: 5000,
      });
      return 0;
    }
  };

  const CELL_W = 56;
  const timelineWidth = timeSlots.length * CELL_W;

  useEffect(() => {
    document.body.style.backgroundColor = "white";

    return () => {
      document.body.style.backgroundColor = "hsl(144deg 48.39% 93.92%)";
    };
  }, []);

  const handleUpdateDoctorSlot = async (
    nextCap?: number | undefined,
    slot?: ScheduleSlotDto | undefined,
    status?: "O" | "C" | undefined,
  ): Promise<
    { id: number; capacity: number; status: "O" | "C" } | undefined
  > => {
    if (!slot?.id) throw new Error("Slot không hợp lệ.");

    var currentStatus =
      slot.status && slot.status !== SlotStatus.BLOCKED ? "O" : "C";
    // build payload luôn đủ cap + status
    const payload =
      nextCap !== undefined
        ? buildUpdateSchedulePayload(slot, {
            capacity: nextCap,
            status: currentStatus,
          })
        : buildUpdateSchedulePayload(slot, { status: status ?? "O" });

    try {
      await updateSchedule.mutateAsync(payload);

      toast({
        variant: "success",
        title: "Cập nhật thành công",
        description: "",
        duration: 3000,
      });

      return payload;
    } catch (err: any) {
      const errorMsg = await toUserMessage(err);
      console.error("Lưu lịch khám thất bại:", errorMsg);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: errorMsg,
        duration: 3000,
      });
    }
  };

  const onChangeTimeRangeStatus = async (
    timeRange: TimeRangeSlotDto,
    status: "O" | "C",
  ) => {
    try {
      await updateTimeRangeStatus.mutateAsync({
        clinicId: timeRange.clinicExtId!,
        localDate: format(timeRange.startTime!, "dd/MM/yyyy"),
        startTime: format(timeRange.startTime!, "HH:mm"),
        endTime: format(timeRange.endTime!, "HH:mm"),
        status: status,
      });

      toast({
        variant: "success",
        title: `Đã ${status === "O" ? "mở" : "đóng"} khung giờ`,
        description: "",
        duration: 3000,
      });
    } catch (err: any) {
      const msg = await toUserMessage(err);
      console.error("Cập nhật trạng thái khung giờ thất bại:", msg);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: msg,
        duration: 3000,
      });
    }
  };

  const handleDeleteDoctorSlot = async (slot: ScheduleSlotDto) => {
    try {
      await deleteDoctorSlot.mutateAsync({ id: slot.id! });

      toast({
        variant: "success",
        title: "Xóa thành công",
        description: "",
        duration: 3000,
      });
    } catch (err: any) {
      const msg = await toUserMessage(err);
      console.error("Xóa bác sĩ khỏi khung giờ thất bại:", msg);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: msg,
        duration: 3000,
      });
    }
  };

  const handleDeleteTimeRange = async (timeRange: TimeRangeSlotDto) => {
    try {
      await deleteTimeRange.mutateAsync({
        clinicId: timeRange.clinicExtId!,
        localDate: format(timeRange.startTime!, "dd/MM/yyyy"),
        startTime: format(timeRange.startTime!, "HH:mm"),
        endTime: format(timeRange.endTime!, "HH:mm"),
      });

      toast({
        variant: "success",
        title: "Xóa khung giờ thành công",
        description: "",
        duration: 3000,
      });
    } catch (err: any) {
      const msg = await toUserMessage(err);
      console.error("Xóa khung giờ thất bại:", msg);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: msg,
        duration: 3000,
      });
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {overlayVisible && (
        <div className="fixed inset-0 z-50 touch-none overscroll-contain">
          <div className="absolute inset-0 bg-white/40" aria-hidden="true" />
          <div className="relative w-full h-full flex items-center justify-center">
            <LoadingIndicator />
          </div>
        </div>
      )}

      <div className="sticky top-[60px] bg-white z-[11]">
        <ScheduleNavBar
          currentDate={currentDate}
          // viewMode={viewMode}
          onDateChange={setCurrentDate}
          onViewModeChange={setViewMode}
          onAddSlot={handleAddSlot}
        />

        <FilterToolbar
          selectedDoctor={selectedDoctor}
          selectedRoom={selectedRoom}
          onDoctorChange={setSelectedDoctor}
          onRoomChange={setSelectedRoom}
          onDoctorSearchChange={setDoctorKeyword}
          onClearFilters={handleClearFilters}
          doctorOptions={doctorOptions}
          clinicOptions={clinicOptions}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden md:overflow-visible">
        <WeekOverview
          currentDate={currentDate}
          doctors={allDoctors}
          doctorSuggestions={weekOverviewDoctorSuggestions}
          isDoctorSearchLoading={isWeekOverviewDoctorLoading}
          onDoctorSearchChange={setWeekOverviewDoctorKeyword}
          rooms={filteredRooms}
          slots={filteredSlots}
          onDayClick={handleDayClick}
          onCreateSlot={handleCreateSlot}
          onUpdateTimeRange={handleUpdateTimeRange}
          handleUpdateDoctorSlot={handleUpdateDoctorSlot}
          onChangeTimeRangeStatus={onChangeTimeRangeStatus}
          handleDeleteDoctorSlot={handleDeleteDoctorSlot}
          handleDeleteTimeRange={handleDeleteTimeRange}
        />
        {/* {viewMode === "day" ? (
          // <TimelineView
          //   date={currentDate}
          //   doctors={filteredDoctors}
          //   rooms={filteredRooms}
          //   slots={filteredSlots}
          //   onSlotClick={handleSlotClick}
          //   onEmptySlotClick={handleEmptySlotClick}
          //   startHour={startHour}
          //   endHour={endHour}
          //   intervalMinutes={intervalMinutes}
          // />
          
        ) : (
          <WeekOverview
            currentDate={currentDate}
            doctors={filteredDoctors}
            rooms={filteredRooms}
            slots={filteredSlots}
            onDayClick={handleDayClick}
            onCreateSlot={handleCreateSlot}
            onUpdateTimeRange={handleUpdateTimeRange}
            handleUpdateDoctorSlot={handleUpdateDoctorSlot}
          />
        )} */}
      </div>

      {createSidebarOpen && (
        // <CreateScheduleOneTabModal onClose={()=>setCreateSidebarOpen(false)}/>
        <CreateSlotSidebar
          currentDate={currentDate}
          form={form}
          selectedTimeSlot={selectedTimeSlot}
          onTimeSlotClick={handleTimeSlotClick}
          onDateToggle={handleDateToggle}
          onSubmit={handleCreateSlot}
          conflicts={conflicts}
          hasConflicts={hasConflicts}
          isCheckingConflicts={isCheckingConflicts}
          onClose={() => {
            setCreateSidebarOpen(false);
            setCreatingSlot(undefined);
            form.reset();
            setSelectedTimeSlot(null);
            setSidebarDoctorKeyword("");
          }}
          doctorSuggestions={sidebarDoctors}
          isDoctorLoading={isSidebarDoctorLoading}
          onDoctorSearchChange={setSidebarDoctorKeyword}
          clinicOptions={clinicOptions}
        />
      )}

      {/* <SlotSidebar
        isOpen={sidebarOpen}
        slot={editingSlot}
        doctors={doctors}
        rooms={clinics}
        localDate={currentDate}
        onClose={() => {
          setSidebarOpen(false);
          setEditingSlot(undefined);
        }}
        onSave={handleSaveSlot}
        onDelete={editingSlot?.id ? handleDeleteSlot : undefined}
        doctorOptions={doctorOptions}
        clinicOptions={clinicOptions}
      /> */}
    </div>
  );
}
